/**
 * Die Ansicht „Absender" im Bereich *Dokumente* (`?view=senders`, Spec 24c,
 * Oberfläche E) — **kein** eigener Bereich, kein Reiter: `routes/Dokumente.tsx`
 * rendert diese Komponente an der Stelle der Dokumentliste, wenn `view ===
 * 'senders'`. Nur Web (`docs/parity.md`, gewollt) — Absender entstehen auf
 * iOS ausschließlich aus dem Dokument heraus.
 *
 * Eigener Kopf (Titel, Suche, „+ Absender") statt der geteilten `Dokumente`-
 * Kopfzeile, weil Suchfeld und primäre Aktion hier andere sind — dieselbe
 * Selbstständigkeit wie `DocumentList`, das „+ Dokument" ebenfalls selbst
 * trägt.
 */

import { useState } from 'react'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { FIELD_CLASS } from '@/components/FormField'
import { useInfiniteSendersList } from '@/features/documents/api'
import type { SenderRow } from '@/features/documents/api'
import { senderCategoryDisplay, senderIdentifiersSummary, senderSubjectDisplay } from '@/features/documents/labels'
import { SenderSheet } from '@/features/documents/SenderSheet'
import { useIsDesktopViewport } from '@/lib/viewport'

type SheetState = { readonly mode: 'create' } | { readonly mode: 'edit'; readonly sender: SenderRow }

export function SenderList() {
  const [q, setQ] = useState('')
  const [sheetState, setSheetState] = useState<SheetState | null>(null)
  const isDesktop = useIsDesktopViewport()

  const query = useInfiniteSendersList({ q })
  const senders = query.data?.pages.flatMap((page) => page.items) ?? []
  const editingSender = sheetState?.mode === 'edit' ? sheetState.sender : null

  const addButton = (
    <button
      type="button"
      data-testid="sender-add"
      onClick={() => {
        setSheetState({ mode: 'create' })
      }}
      className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out)"
    >
      + Absender
    </button>
  )

  return (
    <div className="flex h-full flex-col">
      <header className="border-border flex min-h-(--row-h) flex-wrap items-center justify-between gap-2 border-b px-(--page-x) py-2">
        <h1 id="senders-heading" className="text-page font-(--font-weight-page)">
          Absender
        </h1>
        <label htmlFor="sender-search" className="sr-only">
          Absender durchsuchen
        </label>
        <input
          id="sender-search"
          type="search"
          data-testid="sender-search"
          placeholder="Name oder Alias"
          value={q}
          onChange={(event) => {
            setQ(event.target.value)
          }}
          className={`w-full max-w-64 text-text ${FIELD_CLASS}`}
        />
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto" data-testid="senders-list">
        {query.isPending && <SkeletonRows testId="senders-list-loading" rows={6} />}

        {query.isError && (
          <ErrorState
            testId="senders-list-error"
            retryTestId="senders-list-retry"
            error={query.error}
            onRetry={() => {
              void query.refetch()
            }}
          />
        )}

        {query.isSuccess && senders.length === 0 && (
          <EmptyState
            testId="senders-list-empty"
            title="Noch kein Absender angelegt."
            hint="Absender anlegen — dann erkennt die App seine Post."
            action={addButton}
          />
        )}

        {query.isSuccess && senders.length > 0 && (
          <>
            <div className="flex justify-end px-(--page-x) py-2">{addButton}</div>
            <ul>
              {senders.map((sender) =>
                isDesktop ? (
                  <SenderDesktopRow
                    key={sender.id}
                    sender={sender}
                    onOpen={() => {
                      setSheetState({ mode: 'edit', sender })
                    }}
                  />
                ) : (
                  <SenderMobileCard
                    key={sender.id}
                    sender={sender}
                    onOpen={() => {
                      setSheetState({ mode: 'edit', sender })
                    }}
                  />
                ),
              )}
            </ul>
            {query.hasNextPage && (
              <button
                type="button"
                data-testid="senders-list-more"
                disabled={query.isFetchingNextPage}
                onClick={() => {
                  void query.fetchNextPage()
                }}
                className="border-border text-text m-2 self-start border px-3"
              >
                Mehr laden
              </button>
            )}
          </>
        )}
      </div>

      <SenderSheet
        open={sheetState !== null}
        onOpenChange={(open) => {
          if (!open) setSheetState(null)
        }}
        sender={editingSender}
      />
    </div>
  )
}

interface SenderRowProps {
  readonly sender: SenderRow
  readonly onOpen: () => void
}

function SenderDesktopRow({ sender, onOpen }: SenderRowProps) {
  const identifiers = senderIdentifiersSummary(sender)
  return (
    <li
      data-testid={`sender-row-${sender.id}`}
      className="border-border flex h-(--row-h) items-center gap-3 border-b px-(--page-x) last:border-b-0"
    >
      <button type="button" onClick={onOpen} className="flex flex-1 items-center gap-3 text-left">
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-body text-text truncate font-(--font-weight-section)">{sender.name}</span>
          {sender.aliases.length > 0 && <span className="text-label text-muted truncate">{sender.aliases.join(', ')}</span>}
        </span>
        <span className="text-label text-text w-32 truncate">{senderCategoryDisplay(sender)}</span>
        <span className="text-label text-text w-44 truncate">{senderSubjectDisplay(sender)}</span>
        <span className="text-label text-muted w-56 truncate">{identifiers}</span>
        <span data-numeric className="text-body text-text w-16 text-right">
          {sender.document_count}
        </span>
      </button>
    </li>
  )
}

function SenderMobileCard({ sender, onOpen }: SenderRowProps) {
  return (
    <li data-testid={`sender-row-${sender.id}`} className="border-border border-b px-(--page-x) py-2 last:border-b-0">
      <button type="button" onClick={onOpen} className="flex w-full items-start justify-between gap-2 text-left">
        <span className="flex min-w-0 flex-col">
          <span className="text-body text-text truncate font-(--font-weight-section)">{sender.name}</span>
          <span className="text-label text-muted truncate">
            {senderCategoryDisplay(sender)} · {senderSubjectDisplay(sender)}
          </span>
        </span>
        <span data-numeric className="text-text shrink-0">
          {sender.document_count}
        </span>
      </button>
    </li>
  )
}
